import axios from "axios";
import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Container,
  Spinner,
} from "react-bootstrap";
import "./voucher.css";
import { CircularProgress } from "@mui/material";
import { AuthContext } from "../../../Context/AuthContext";
import { FaPlus } from "react-icons/fa6";
import { Authorization } from "../../../Context/Authorization";

const VoucherTypeMapping = () => {
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedVoucherType, setSelectedVoucherType] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [mappings, setMappings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentMappingId, setCurrentMappingId] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state for initial data fetch
  const [submitting, setSubmitting] = useState(false); // Loading state for form submission
  const { permissions } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  // Fetch voucher types, accounts, and mappings when the component mounts
  useEffect(() => {
    const fetchVoucherData = async () => {
      try {
        const Config = Authorization();
        const response = await axios.get(
          `${Api}/api/voucher-type-mappings`,
          Config
        );
        setMappings(response.data.voucherTypeMappings);
      } catch (error) {
        console.error("Error fetching voucher type mappings", error);
      }
    };

    const fetchVoucherTypes = async () => {
      try {
        const Config = Authorization();
        const response = await axios.get(`${Api}/api/voucher-types`, Config);
        setVoucherTypes(response.data.voucherTypes);
      } catch (error) {
        console.error("Error fetching voucher types", error);
      }
    };

    const fetchAccounts = async () => {
      try {
        const Config = Authorization();
        const response = await axios.get(`${Api}/api/accounts`, Config);
        setAccounts(response.data.data);
      } catch (error) {
        console.error("Error fetching accounts", error);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchVoucherTypes(),
        fetchAccounts(),
        fetchVoucherData(),
      ]);
      setLoading(false);
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const newMapping = {
      voucherType: selectedVoucherType,
      accountId: selectedAccountId,
    };

    if (isEditing) {
      try {
        const Config = Authorization(); // Assuming Authorization() returns headers or config
        const response = await axios.put(
          `${Api}/api/voucher-type-mappings/${currentMappingId}`,
          newMapping, // This is the data you want to send
          Config // This is the config (headers, etc.)
        );
        const updatedMappings = mappings.map((mapping) =>
          mapping._id === currentMappingId
            ? response.data.voucherTypeMapping
            : mapping
        );
        setMappings(updatedMappings);
        setIsEditing(false);
        setShowModal(false);
      } catch (error) {
        console.error("Error updating mapping", error);
      }
    } else {
      try {
        const response = await axios.post(
          `${Api}/api/voucher-type-mappings`,
          newMapping, // The new mapping data
          Authorization() // Assuming Authorization() is the config to be passed
        );
        setMappings([...mappings, response.data.voucherTypeMapping]);
        setShowModal(false);
      } catch (error) {
        console.error("Error adding mapping", error);
      }
    }
    setSubmitting(false);
  };

  const handleDeleteVoucherType = async (id) => {
    try {
      const response = await axios.delete(
        `${Api}/api/voucher-type-mappings/${id}`
      );
      setMappings(response.data.voucherTypeMappings);
    } catch (error) {
      console.error("Error deleting mapping", error);
    }
  };

  const handleEditClick = (mapping) => {
    setSelectedVoucherType(mapping.voucherType._id);
    setSelectedAccountId(mapping.accountId._id);
    setCurrentMappingId(mapping._id);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedVoucherType("");
    setSelectedAccountId("");
  };

  return (
    <div className="voucher-mapping-wrapper">
      <div className="page-header">
        <h3 className="page-title"> Voucher Type Mapping </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Voucher Type Mapping
            </li>
          </ol>
        </nav>
      </div>

      <Container>
        {loading ? (
          <div className="loading-container">
            <CircularProgress />
          </div>
        ) : (
          <>
            <Modal show={showModal} onHide={handleCloseModal}>
              <Modal.Header>
                <Modal.Title id="example-modal-sizes-title-lg">
                  {isEditing ? "Edit Previous Mapping" : "Add New Mapping"}
                </Modal.Title>
                <Button
                  variant="outline-danger"
                  onClick={handleCloseModal}
                  style={{ marginLeft: "auto" }}
                >
                  X
                </Button>
              </Modal.Header>
              <Modal.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group controlId="formVoucherType">
                    <Form.Label>Voucher Type:</Form.Label>
                    <div className="input-container">
                      <Form.Control
                        as="select"
                        value={selectedVoucherType}
                        onChange={(e) => setSelectedVoucherType(e.target.value)}
                      >
                        <option value="">Select Voucher Type</option>
                        {voucherTypes.map((type) => (
                          <option key={type._id} value={type._id}>
                            {type.voucherName}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formAccountId">
                    <Form.Label>Account ID:</Form.Label>
                    <div className="input-container">
                      <Form.Control
                        as="select"
                        value={selectedAccountId}
                        onChange={(e) => setSelectedAccountId(e.target.value)}
                      >
                        <option value="">Select Account ID</option>
                        {accounts.map((account) => (
                          <option key={account._id} value={account._id}>
                            {account.accountName}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Form.Group>
                  <button
                    type="submit"
                    className="btn btn-outline-primary btn-icon-text mt-3"
                    disabled={submitting}
                  >
                    <i className="mdi mdi-file-check btn-icon-prepend"></i>
                    {submitting ? (
                      <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Submitting...</span>
                      </Spinner>
                    ) : isEditing ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </button>
                </Form>
              </Modal.Body>
            </Modal>

            <div className="card">
              <div className="card-body">
                <div className="row icons-list">
                  {permissions.includes("AddVoucherTypeMappings") && (
                    <div className="Add_btn">
                      <button
                        type="button"
                        className="btn btn-inverse-success btn-icon"
                        onClick={() => setShowModal(true)}
                      >
                        <FaPlus />
                      </button>
                    </div>
                  )}
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr className="table table-hover">
                          <th>Voucher Type</th>
                          <th>Account ID</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappings.map((mapping, index) => (
                          <tr key={index}>
                            <td>{mapping.voucherType.voucherName}</td>
                            <td>{mapping.accountId.accountName}</td>
                            <td>
                              <i
                                className="mdi mdi-pen"
                                style={{
                                  fontSize: "1.5rem",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleEditClick(mapping)}
                              />

                              <i
                                className="mdi mdi-delete"
                                style={{
                                  fontSize: "1.5rem",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleDeleteVoucherType(mapping._id)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default VoucherTypeMapping;
